// @flow

import * as React from "react";
import { View } from "@components";

import type { WidgetProps } from "@rjsf/utils";

type Props = WidgetProps & {
  options: Object,
};

const readFileAsDataURL = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });

const getFileNameFromDataURL = (dataUrl: string, index: number): string => {
  const match = dataUrl && dataUrl.match(/;name=([^;]+);base64,/);
  return match ? decodeURIComponent(match[1]) : `File ${index + 1}`;
};

export function FileInputWidget(props: Props) {
  const isMultiple = props.options?.multiple;
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const handleChange = async (event: SyntheticEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const files = input.files;

    if (!files || files.length === 0) {
      props.onChange(isMultiple ? [] : null);
      return;
    }

    if (isMultiple) {
      const results = [];
      for (let i = 0; i < files.length; i++) {
        const dataUrl = await readFileAsDataURL(files[i]);
        const withName = dataUrl.replace(
          ";base64,",
          `;name=${encodeURIComponent(files[i].name)};base64,`,
        );
        results.push(withName);
      }
      const existing = Array.isArray(props.value) ? props.value : [];
      props.onChange([...existing, ...results]);
    } else {
      const dataUrl = await readFileAsDataURL(files[0]);
      const withName = dataUrl.replace(
        ";base64,",
        `;name=${encodeURIComponent(files[0].name)};base64,`,
      );
      props.onChange(withName);
    }
  };

  const handleClearFile = (index?: number) => {
    if (isMultiple && Array.isArray(props.value) && typeof index === "number") {
      const updated = props.value.filter((_, i) => i !== index);
      props.onChange(updated.length > 0 ? updated : []);
    } else {
      props.onChange(isMultiple ? [] : null);
    }
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const inputProps: Object = {
    ...props,
    ...props.options,
    onChange: handleChange,
  };

  delete inputProps.readonly;
  delete inputProps.formContext;
  delete inputProps.autofocus;
  delete inputProps.rawErrors;
  delete inputProps.registry;
  delete inputProps.schema;
  delete inputProps.options;
  delete inputProps.label;
  delete inputProps.labelposition;
  delete inputProps.enumOptions;
  delete inputProps.editable;
  delete inputProps.value;
  delete inputProps.fileName;
  delete inputProps.fileNames;

  if (isMultiple) {
    const values = Array.isArray(props.value) ? props.value : [];
    const fileNames = props.options?.fileNames || [];

    return (
      <View direction="row" gap={3} alignItems="center" className="flex-wrap">
        {!inputProps.disabled && (
          <input type="file" {...inputProps} multiple style={{ width: "96px" }} />
        )}
        {values.map((val, i) => {
          const isPreview = val && val.startsWith("https:");
          const label = isPreview
            ? fileNames[i] || `File ${i + 1}`
            : getFileNameFromDataURL(val, i);

          return (
            <View key={i} direction="row" gap={2} alignItems="center">
              {isPreview ? (
                <a href={val} target="_blank" rel="noopener noreferrer" className="pointer">
                  {label}
                </a>
              ) : (
                <span>{label}</span>
              )}
              {!inputProps.disabled && (
                <span
                  onClick={() => handleClearFile(i)}
                  className="material-icons pointer text-color-red"
                >
                  close
                </span>
              )}
            </View>
          );
        })}
      </View>
    );
  }

  const hasPreview = props.value && props.value.startsWith("https:");
  return (
    <View direction="row" gap={3} alignItems="center">
      {hasPreview ? (
        <a href={props.value} target="_blank" rel="noopener noreferrer" className="pointer">
          {props.options?.fileName || "View file"}
        </a>
      ) : (
        <input ref={inputRef} type="file" {...inputProps} className={props.className} />
      )}
      {Boolean(props.value) && !inputProps.disabled && (
        <span onClick={() => handleClearFile()} className="material-icons pointer text-color-red">
          close
        </span>
      )}
    </View>
  );
}

export default FileInputWidget;
